import { z } from 'zod'

export const deductionSchema = z.object({
  employeeId: z.string().min(1, 'Employee is required'),
  amount: z.number().min(0.01, 'Amount must be at least 0.01'),
  note: z.string().optional(),
})

export type DeductionFormValues = z.infer<typeof deductionSchema>

export const adjustmentSchema = z.object({
  employeeId: z.string().min(1, 'Employee is required'),
  amount: z.number().min(0.01, 'Amount must be at least 0.01'),
  note: z.string().optional(),
})

export type AdjustmentFormValues = z.infer<typeof adjustmentSchema>

export const refundRequestSchema = z.object({
  amount: z.number().min(0.01, 'Amount must be at least 0.01'),
  note: z.string().optional(),
})

export type RefundRequestFormValues = z.infer<typeof refundRequestSchema>

/**
 * Refund schema bound to the current held balance so the amount can't exceed it
 * client-side (the backend rejects over-balance too, but this gives an inline error).
 */
export function makeRefundRequestSchema(maxBalance: number) {
  return z.object({
    amount: z
      .number({ error: 'Amount is required' })
      .min(0.01, 'Amount must be at least 0.01')
      .max(maxBalance, `Cannot exceed the current balance (${maxBalance.toLocaleString()})`),
    note: z.string().optional(),
  })
}

/** Safely reads the configured monthly deduction amount from company.settings (Record<string,unknown>). */
export function getMonthlyDeductionAmount(settings?: Record<string, unknown>): number | undefined {
  const payroll = settings?.payroll
  if (!payroll || typeof payroll !== 'object') return undefined
  const amount = (payroll as Record<string, unknown>).monthlySecurityDeductionAmount
  return typeof amount === 'number' && amount > 0 ? amount : undefined
}
